// import type { GetStaticPropsContext } from 'next'; // not importable from flowtyped atm
import { get } from 'lodash';
import moment from 'moment';
import Uri from 'urijs';
import type { Request } from 'express';
import domainsService from '@clubmed/domains-service';
import { httpHeaders, url } from '@clubmed/utils';
import { fetchFeatureFlippingKeysFromCms } from 'Context/fetchFeatureFlippingKeysFromCms';
import { initAppContext } from 'Context/initAppContext';
import { getCurrentLocale, getCurrentUri } from 'Utils/context/contextUtils';
import { getDeviceByUA } from 'Components/utils/responsive/Responsive.helper';
import { DEVICES } from 'Components/utils/responsive/Responsive.constants';
const AKAMAI_DEVICE_DETECTION = 'x-akamai-device-characteristics';

const mapAkamaiDeviceHeader = (header) => {
  const [key, value] = header.trim().split('=');

  try {
    return [key, JSON.parse(value)];
  } catch {
    return [key, value.trim()];
  }
};

const parseAkamaiDeviceHeader = (akamaiHeader) => {
  if (!akamaiHeader) {
    return null;
  }

  return Object.fromEntries(akamaiHeader.split(';').map(mapAkamaiDeviceHeader));
};

const getDevice = (req: Request) => {
  const deviceDetection = parseAkamaiDeviceHeader(req.headers[AKAMAI_DEVICE_DETECTION]);

  if (!deviceDetection) {
    return getDeviceByUA(req.headers['user-agent']);
  }

  if (deviceDetection.is_mobile === true) {
    return DEVICES.MOBILE;
  }

  return DEVICES.DESKTOP;
};

export const createAppContextFromProps = (appContext: AppContext): AppContext => {
  const uri = new Uri(appContext.originalUrl);
  const hostname = uri.hostname();
  const domainServiceForHostname = domainsService.forHostname(hostname);
  return {
    ...appContext,
    availableLocales: domainServiceForHostname.getAllLocales(),
    defaultLocale: domainServiceForHostname.getDefaultLocale(),
    ...initAppContext(appContext.originalUrl, appContext),
  };
};
export const createAppContextFromReq = async (req: Request): Promise<AppContext> => {
  const requestId = req.headers[httpHeaders.X_REQUEST_ID] || req.id;
  const uri = getCurrentUri(req);
  const queryParameters = url.decodeQueryParameters(uri.query(true));
  const requestedLocale = queryParameters.locale;
  const hostname = uri.hostname();
  const domainServiceForHostname = domainsService.forHostname(hostname);
  const availableLocales = domainServiceForHostname.getAllLocales();
  const defaultLocale = domainServiceForHostname.getDefaultLocale();
  const locale = getCurrentLocale(requestedLocale, hostname);
  const host: string = req.get('host');
  const userAgent = req.headers['user-agent'];
  const baseAppContext = {
    availableLocales,
    cookies: req.cookies,
    defaultLocale,
    locale,
    originalUrl: req.protocol + '://' + host + req.originalUrl,
    requestId,
    userAgent,
    device: getDevice(req),
  };
  moment.locale(get(availableLocales, `${locale}.moment`, 'fr'));
  const appContext = { ...baseAppContext, ...initAppContext(uri, baseAppContext) };
  const featureFlippingKeysFromCms = await fetchFeatureFlippingKeysFromCms(appContext);
  return {
    ...appContext,
    ...(typeof featureFlippingKeysFromCms === 'object' && {
      featureFlippingKeysFromCms,
    }),
  };
};
